
import { useState } from "react";
import { KnowledgeEntry } from "../types";
import { Button } from "@/components/ui/button";
import { FileText, Download } from "lucide-react";

interface FilePreviewProps {
  entry: KnowledgeEntry;
}

export function FilePreview({ entry }: FilePreviewProps) {
  const [viewerError, setViewerError] = useState<boolean>(false);

  // Function to handle PDF viewer errors
  const handlePdfError = () => {
    setViewerError(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-center items-center h-[50vh] border rounded-md overflow-hidden">
        {entry.file_type?.includes('pdf') ? (
          !viewerError ? (
            <object 
              data={entry.file_url} 
              type="application/pdf" 
              className="w-full h-full"
              onError={handlePdfError}
            >
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="mb-4">Unable to display PDF directly in the browser.</p>
                <div className="flex space-x-4">
                  <Button 
                    variant="outline"
                    onClick={() => window.open(entry.file_url, '_blank')}
                  >
                    Open in New Tab
                  </Button>
                  <a 
                    href={entry.file_url} 
                    download
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </a>
                </div>
              </div>
            </object>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="mb-4">This PDF file cannot be displayed directly in the browser due to security settings.</p>
              <div className="flex space-x-4">
                <Button 
                  variant="outline"
                  onClick={() => window.open(entry.file_url, '_blank')}
                >
                  Open in New Tab
                </Button>
                <a 
                  href={entry.file_url} 
                  download
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </a>
              </div>
            </div>
          )
        ) : entry.file_type?.includes('image') ? (
          <img 
            src={entry.file_url} 
            alt={entry.title} 
            className="max-w-full max-h-full object-contain" 
          />
        ) : (
          <div className="text-center p-4">
            <p className="mb-4">This file type cannot be previewed directly.</p>
            <a 
              href={entry.file_url} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded"
            >
              Download File
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
